import { createRouter, createWebHistory } from 'vue-router'
import store from '../store'

// Auth Pages
import LoginPage from '../views/auth/LoginPage.vue'
import RegisterPage from '../views/auth/RegisterPage.vue'
import ForgotPassword from '../views/auth/ForgotPassword.vue'
import ResetPassword from '../views/auth/ResetPassword.vue'
import EmailVerificationPage from '../views/auth/EmailVerificationPage.vue'
import TwoFactorSetup from '../views/auth/TwoFactorSetup.vue'
import SetPassword from '../views/auth/SetPassword.vue'

// App Pages
import Home from '../views/Home.vue'
import NotFound from '../views/NotFound.vue'
import Profile from '@/views/Profile.vue'
import Settings from '@/views/Settings.vue'
import AdminPanel from '@/views/AdminPanel.vue'
import Databank from '@/views/Databank.vue'
import CertificatesDatabank from '@/views/databank/CertificatesDatabank.vue'
import MailTemplateDatabank from '@/views/databank/MailTemplateDatabank.vue'
// import SupplierList from '../views/SupplierList.vue' // Removed as the file has been deleted
import RfqList from '../views/RfqList.vue'
import PurchaseOrderList from '../views/PurchaseOrderList.vue'
// Super Admin - Status Management (lazy loaded view)
const StatusManagement = () => import('../views/super-admin/StatusManagement.vue')
const PdfTemplateManagement = () => import('../views/super-admin/PdfTemplateManagement.vue')

// User Pages
import UserList from '../views/users/UserList.vue'
import UserForm from '../views/users/UserForm.vue'



// Routes
const routes = [
  // Auth routes
  {
    path: '/login',
    name: 'Login',
    component: LoginPage,
    meta: { requiresAuth: false, title: 'Login' }
  },
  {
    path: '/register',
    name: 'Register',
    component: RegisterPage,
    meta: { requiresAuth: false, title: 'Register' }
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: ForgotPassword,
    meta: { requiresAuth: false, title: 'Forgot Password' }
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: ResetPassword,
    meta: { requiresAuth: false, title: 'Reset Password' }
  },
  {
    path: '/set-password',
    name: 'SetPassword',
    component: SetPassword,
    meta: { requiresAuth: false, title: 'Set Password' }
  },
  {
    path: '/email-verification',
    name: 'EmailVerification',
    component: EmailVerificationPage,
    meta: { requiresAuth: false, title: 'Email Verification' }
  },
  {
    path: '/auth/2fa-setup',
    name: 'TwoFactorSetup',
    component: TwoFactorSetup,
    meta: { requiresAuth: true, requiresSuperAdmin: true, title: 'Two-Factor Setup' }
  },
  
  // Application routes
  {
    path: '/',
    name: 'Home',
    component: Home,
    meta: { requiresAuth: true, title: 'Home' }
  },
  // Dashboard route removed
  {
    path: '/admin',
    name: 'AdminPanel',
    component: AdminPanel,
    meta: { requiresAuth: true, requiresSuperAdmin: true, title: 'Admin Panel' }
  },
  {
    path: '/super-admin/statuses',
    name: 'StatusManagement',
    component: StatusManagement,
    meta: { requiresAuth: true, requiresSuperAdmin: true, title: 'Status Management' }
  },
  {
    path: '/pdf-templates',
    name: 'PdfTemplateManagement',
    component: PdfTemplateManagement,
    meta: { requiresAuth: true, requiresPdfTemplatesAccess: true, title: 'PDF Templates' }
  },
  {
    path: '/databank',
    name: 'Databank',
    component: Databank,
    meta: { requiresAuth: true, requiresSuperAdmin: true, title: 'Databank' }
  },
  {
    path: '/databank/certificates',
    name: 'CertificatesDatabank',
    component: CertificatesDatabank,
    meta: { requiresAuth: true, requiresSuperAdmin: true, title: 'Certificates' }
  },
  {
    path: '/databank/mail-template',
    name: 'MailTemplateDatabank',
    component: MailTemplateDatabank,
    meta: { requiresAuth: true, requiresSuperAdmin: true, title: 'Mail Template' }
  },
  {
    path: '/databank/user-guide',
    name: 'UserGuideDatabank',
    component: () => import('../views/databank/UserGuideDatabank.vue'),
    meta: { requiresAuth: true, requiresSuperAdmin: true, title: 'User Guide' }
  },
  /* Temporarily removed as SupplierList component has been deleted
  {
    path: '/suppliers',
    name: 'Suppliers',
    component: SupplierList,
    meta: { requiresAuth: true }
  },
  */
  {
    path: '/rfq',
    name: 'RFQ',
    component: RfqList,
    meta: { 
      requiresAuth: true,
      requiredPermissions: ['Supplier.Quotations.ViewDetails', 'Supplier.Quotations.Create', 'Supplier.Quotations.Edit', 'Supplier.Quotations.Approve', 'Supplier.Quotations'],
      title: 'RFQ'
    }
  },
  {
    path: '/rfq-list',
    name: 'RfqList',
    component: () => import('../views/RfqList.vue'),
    meta: { 
      requiresAuth: true,
      requiredPermissions: ['Supplier.Quotations.ViewDetails', 'Supplier.Quotations'],
      title: 'RFQ List'
    }
  },
  {
    path: '/rfq/:id',
    name: 'RfqEdit',
    component: () => import('../views/RfqEdit.vue'),
    meta: { 
      requiresAuth: true,
      requiredPermissions: ['Supplier.Quotations.ViewDetails', 'Supplier.Quotations.Edit', 'Supplier.Quotations'],
      title: 'Edit RFQ'
    }
  },
  {
    path: '/rfq/:id/reject',
    name: 'RfqReject',
    component: () => import('../views/RfqEdit.vue'),
    meta: { 
      requiresAuth: true,
      requiredPermissions: ['Supplier.Quotations.ViewDetails', 'Supplier.Quotations.Edit', 'Supplier.Quotations'],
      title: 'Decline RFQ'
    }
  },
  {
    path: '/purchase-order',
    name: 'PurchaseOrder',
    component: PurchaseOrderList,
    meta: { 
      requiresAuth: true,
      requiredPermissions: ['Supplier.PurchaseOrders.ViewDetails', 'Supplier.PurchaseOrders.Create', 'Supplier.PurchaseOrders.Edit', 'Supplier.PurchaseOrders.Approve', 'Supplier.PurchaseOrders'],
      title: 'Sales Order'
    }
  },
  {
    path: '/purchase-order-list',
    name: 'PurchaseOrderList',
    component: () => import('../views/PurchaseOrderList.vue'),
    meta: { 
      requiresAuth: true,
      requiredPermissions: ['Supplier.PurchaseOrders.ViewDetails', 'Supplier.PurchaseOrders'],
      title: 'Purchase Order List'
    }
  },
  {
    path: '/purchase-order/:id',
    name: 'PurchaseOrderEdit',
    component: () => import('../views/PurchaseOrderEdit.vue'),
    meta: { 
      requiresAuth: true,
      requiredPermissions: ['Supplier.PurchaseOrders.ViewDetails', 'Supplier.PurchaseOrders.Edit', 'Supplier.PurchaseOrders'],
      title: 'Purchase Order Details'
    }
  },
  {
    path: '/profile',
    name: 'Profile',
    component: Profile,
    meta: { requiresAuth: true, requiresNonLookupAccess: true, title: 'Profile' }
  },
  {
    path: '/lookup',
    name: 'LookUp',
    component: () => import('../views/LookUp.vue'),
    meta: { requiresAuth: true, requiresLookupAccess: true, title: 'LookUp' }
  },
  {
    path: '/quotation-mail-recipients',
    name: 'QuotationMailRecipients',
    component: () => import('../views/quotation-mail-recipients/QuotationMailRecipients.vue'),
    meta: { requiresAuth: true, requiresLookupAccess: true, title: 'Quotation Mail Recipients' }
  },
  
  // Mail Tracking routes (for buyer tenants)
  {
    path: '/mail-tracking',
    name: 'MailTrackingDashboard',
    component: () => import('../views/mail-tracking/MailTrackingDashboard.vue'),
    meta: { requiresAuth: true, requiresLookupAccess: true, title: 'Mail Tracking' }
  },
  {
    path: '/mail-tracking/list',
    name: 'MailTrackingList',
    component: () => import('../views/mail-tracking/MailTrackingList.vue'),
    meta: { requiresAuth: true, requiresLookupAccess: true, title: 'Mail Tracking List' }
  },
  {
    path: '/mail-tracking/details/:id',
    name: 'MailTrackingDetails',
    component: () => import('../views/mail-tracking/MailTrackingDetails.vue'),
    meta: { requiresAuth: true, requiresLookupAccess: true, title: 'Mail Tracking Details' }
  },
  {
    path: '/settings',
    name: 'Settings',
    component: Settings,
    meta: { requiresAuth: true, title: 'Settings' }
  },
  {
    path: '/culture',
    name: 'CultureSettings',
    component: Settings,
    meta: { requiresAuth: true, title: 'Culture' }
  },
  {
    path: '/help',
    name: 'Help',
    component: () => import('../views/Help.vue'),
    meta: { requiresAuth: true, title: 'Help' }
  },
  {
    path: '/notifications',
    name: 'Notifications',
    component: () => import('../views/NotificationList.vue'),
    meta: { requiresAuth: true, title: 'Notifications' }
  },
  
  // User Management routes
  {
    path: '/users',
    name: 'UserList',
    component: UserList,
    meta: { 
      requiresAuth: true,
      requiredPermissions: ['Supplier.Users', 'Supplier.Administration'],
      title: 'Users'
    }
  },
  {
    path: '/users/create',
    name: 'CreateUser',
    component: UserForm,
    meta: { 
      requiresAuth: true,
      requiredPermissions: ['Supplier.Users', 'Supplier.Administration'],
      title: 'Create User'
    }
  },
  {
    path: '/users/edit/:id',
    name: 'EditUser',
    component: UserForm,
    meta: { 
      requiresAuth: true,
      requiredPermissions: ['Supplier.Users', 'Supplier.Administration'],
      title: 'Edit User'
    }
  },
  

  
  // Catch all (404)
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: NotFound,
    meta: { title: 'Page Not Found' }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    // Always scroll to top
    return { top: 0 };
  }
})

// Dynamic title functionality
router.afterEach((to) => {
  // Get the title from the route meta
  const title = to.meta.title || to.name
  
  // Set the document title
  if (title && title !== 'Home') {
    document.title = `VESPROC - ${title}`
  } else {
    document.title = 'VESPROC'
  }
})

// Navigation Guards
router.beforeEach((to, from, next) => {
  const isAuthenticated = store.getters['auth/isAuthenticated']
  const currentUser = store.getters['auth/currentUser']
  const hasAnyPermission = store.getters['auth/hasAnyPermission']
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)
  const requiresLookupAccess = to.matched.some(record => record.meta.requiresLookupAccess)
  const requiresNonLookupAccess = to.matched.some(record => record.meta.requiresNonLookupAccess)
  const requiresSuperAdmin = to.matched.some(record => record.meta.requiresSuperAdmin)
  const requiresPdfTemplatesAccess = to.matched.some(record => record.meta.requiresPdfTemplatesAccess)
  
  // Get required permissions for the route
  const requiredPermissions = to.matched
    .filter(record => record.meta.requiredPermissions)
    .map(record => record.meta.requiredPermissions)
    .flat()
  
  // Check if user has buyer tenant access
  const hasBuyerTenantAccess = currentUser?.editionId?.toUpperCase() === '98CAF202-9D8E-B62F-01C1-39FF3E9F71A0'

  // Special edition access to PDF Template Management
  const allowedPdfTemplatesEditionId = 'B0CB311A-A4D0-65B4-DF1B-39FF3E9F716E'
  const editionMatchesPdfTemplatesAccess = currentUser?.editionId?.toUpperCase() === allowedPdfTemplatesEditionId
  const printTemplatePermissions = [
    'Supplier.PrintTemplates',
    'Supplier.PrintTemplates.Create',
    'Supplier.PrintTemplates.Edit',
    'Supplier.PrintTemplates.Delete'
  ]
  const hasPrintTemplatesPermission = hasAnyPermission(printTemplatePermissions)
  const basePdfTemplatesTenantAccess = currentUser?.isImpersonating
    ? editionMatchesPdfTemplatesAccess
    : Boolean(currentUser?.isSuperAdmin || editionMatchesPdfTemplatesAccess)
  // If user is impersonating, only allow based on the impersonated user's editionId.
  const hasPdfTemplatesAccess = basePdfTemplatesTenantAccess && (Boolean(currentUser?.isSuperAdmin) || hasPrintTemplatesPermission)
  
  // If route requires auth and user is not authenticated
  if (requiresAuth && !isAuthenticated) {
    // Save the original URL to redirect after login
    next({
      path: '/login',
      query: { redirect: to.fullPath }
    })
  } 
  // If route requires super admin access and user is not super admin
  else if (requiresSuperAdmin && !currentUser?.isSuperAdmin) {
    
    next('/profile') // Redirect to profile if not super admin
  }
  // If user is impersonating and trying to access admin panel, redirect appropriately
  else if (requiresSuperAdmin && currentUser?.isImpersonating) {
    // Check if impersonated user is a buyer tenant
    if (hasBuyerTenantAccess) {
      window.location.href = '/lookup'; // Redirect buyer tenants to lookup page
    } else {
      window.location.href = '/profile'; // Redirect normal users to profile page
    }
    return;
  }
  // If route requires special PDF templates access and user doesn't have it
  else if (requiresPdfTemplatesAccess && !hasPdfTemplatesAccess) {
    next('/settings')
  }
  // If route requires specific permissions and user doesn't have any of them
  else if (requiredPermissions.length > 0 && !hasAnyPermission(requiredPermissions)) {
    
    next('/profile') // Redirect to profile if missing permissions
  }
  // If route requires buyer tenant access and user doesn't have it
  else if (requiresLookupAccess && !hasBuyerTenantAccess) {
    next('/profile') // Redirect to profile if no buyer tenant access
  }
  // If route requires non-buyer tenant access and user is a buyer tenant
  else if (requiresNonLookupAccess && hasBuyerTenantAccess) {
    next('/lookup') // Redirect buyer tenants to their main page
  }
  // If user is authenticated and trying to access auth page (except reset-password which can be accessed with valid token)
  else if (isAuthenticated && (to.path === '/login' || to.path === '/register' || to.path === '/forgot-password')) {
    next('/profile')
  } 
  // Otherwise proceed normally
  else {
    next()
  }
})

export default router 